/**
 * テロップ (captions) の並び順の正規化: 開始秒 (同値なら終了秒) の昇順に保つ。
 *
 * 右パネルのカード・タイムラインのテロップ行・AI へ渡す列挙・重なり順はすべて配列順を使うので、
 * 表示だけを並び替えるのではなく配列そのものを常に時系列順にしておく (2026-10-14)。
 * 安定ソートなので同じ開始・終了秒のものは元の順を保つ。既に並んでいれば同じ配列を返す
 * (参照が変わらないので無駄な再描画・履歴差分を作らない)。
 *
 * 通す入口: validateProject (読込・AI 適用)、applyTimelineWithRipple (カード編集・分割・移動)、
 * moveCaptionToItem (別シーンへの移動)、applyAlignedCaptions (音声同期)。
 */
import type { Caption, TimelineItem } from './project';

function compareCaptions(a: Caption, b: Caption): number {
  return a.startSec - b.startSec || a.endSec - b.endSec;
}

export function isCaptionsSorted(captions: readonly Caption[]): boolean {
  for (let i = 1; i < captions.length; i++) {
    if (compareCaptions(captions[i - 1]!, captions[i]!) > 0) return false;
  }
  return true;
}

export function sortCaptionsByStart<T extends Caption>(captions: T[]): T[] {
  if (isCaptionsSorted(captions)) return captions;
  return captions.slice().sort(compareCaptions);
}

/** タイムラインの全シーンのテロップを時系列順に揃える (並び済みのシーンは同じオブジェクトのまま)。 */
export function sortTimelineCaptions(timeline: TimelineItem[]): TimelineItem[] {
  let changed = false;
  const next = timeline.map((item) => {
    const captions = sortCaptionsByStart(item.captions);
    if (captions === item.captions) return item;
    changed = true;
    return { ...item, captions };
  });
  return changed ? next : timeline;
}

/**
 * 「テロップを追加」の初期区間: 既存のテロップの最後の終了秒から始め、シーンの終わりまで。
 * 既に末尾までテロップがある (または尺が無い) ときは、末尾に最低 0.5 秒を確保できる位置に戻す。
 * 開始秒 0 で末尾に足していた従来の挙動だと、時系列順に並べた途端に先頭へ飛んでしまうため。
 */
export function nextCaptionSpan(captions: readonly Caption[], itemDurationSec: number): { startSec: number; endSec: number } {
  const minSec = 0.5;
  const lastEnd = captions.reduce((max, caption) => Math.max(max, caption.endSec), 0);
  const startSec = Math.max(0, Math.min(lastEnd, itemDurationSec - minSec));
  const endSec = Math.max(startSec + minSec, itemDurationSec);
  return { startSec: round2(startSec), endSec: round2(endSec) };
}

function round2(sec: number): number {
  return Math.round(sec * 100) / 100;
}
